import React, { useCallback, useEffect } from 'react';
import { AutoCompleteField, Logo, MCButton, MCText, Spinner, TextField, View } from '../basic';
import Avatar from '../basic/Avatar';
import {
  addressObject,
  api,
  events,
  lang,
  remCss,
  remFontCss,
  rgba,
  scssVariables,
  userFullName,
  useScreenDetail,
  validateObj
} from 'src/helpers';
import { QRCodeSVG } from 'qrcode.react';
import LANG_CONST from 'src/helpers/lang/const';
import { popupModel } from '../complex';
import SVGIcon, { BASE_ICONS } from '../svg';
import moment from 'moment';
import AlertManager from '../alert/AlertManager';
import { Link } from 'react-router-dom';
import config from '../../config';
import { InfoAlert } from '../alert/InfoAlert';

const apiHost = config.apiHost();
const apiPath = apiHost + '/api/0/json';

const CARD_STATUS = {
  pending: 'pending',
  processed: 'processed',
  cancelled: 'cancelled',
  delivered: 'delivered',
  cancelled_by_doctor: 'cancelled_by_doctor'
};

export const DoctorCardInput = ({ item, hide, afterSave, model, isEdit }) => {
  const [address, setAddress] = React.useState(item?.address || '');
  const [countryAddress, setCountryAddress] = React.useState(item?.countryAddress || '');
  const [stateAddress, setStateAddress] = React.useState(item?.stateAddress || '');
  const [cityAddress, setCityAddress] = React.useState(item?.cityAddress || '');
  const [zipcode, setZipcode] = React.useState(item?.zipcode || '');
  const [phoneNumber, setPhoneNumber] = React.useState(item?.phoneNumber || '');

  const [invalid, setInvalid] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [date, setDate] = React.useState(item?.date);
  const [step, setStep] = React.useState(!item || isEdit ? 1 : 2);
  const screenDetail = useScreenDetail();

  const addressData = addressObject({
    countryAddress,
    stateAddress,
    cityAddress,
    address,
    zipcode
  });

  const save = useCallback(
    async (e) => {
      e.preventDefault();
      const req = {
        address,
        countryAddress,
        stateAddress,
        cityAddress,
        zipcode,
        phoneNumber,
        id: item?.id
      };
      let invalid = validateObj(req, {
        address: ['required', 'address'],
        countryAddress: ['required', 'countryAddress'],
        cityAddress: ['cityAddress'],
        zipcode: ['zipcode'],
        phoneNumber: ['required', 'phoneNumber']
      });

      if (invalid.length > 0) return setInvalid(invalid);

      setLoading(true);

      const res = await api(item ? '/visitCardOrder/update' : '/visitCardOrder/request', req);

      afterSave && afterSave(res);
      if (!res.error) {
        setDate(res.data.date);
        isEdit ? hide() : setStep(2);
      }

      setLoading(false);
    },
    [
      address,
      afterSave,
      cityAddress,
      countryAddress,
      hide,
      isEdit,
      item,
      phoneNumber,
      stateAddress,
      zipcode
    ]
  );

  return step === 1 ? (
    <div className='mc-popup-contain' style={{ maxWidth: remCss(650), width: '100%' }}>
      <>
        <div className='mc-modal-header'>
          <div className='mc-popup-title'>{lang.t(LANG_CONST.address.delivery_address)}</div>
        </div>

        <div className='mc-modal-body'>
          <div className={'mc-flex-25-col'}>
            <div className={'mc-flex-25 mc-flex-start'}>
              <div className={'mc-grow'}>
                <AutoCompleteField
                  template={6}
                  label={LANG_CONST.address.country}
                  name={'countryAddress'}
                  value={countryAddress}
                  invalid={invalid}
                  onChange={(n, v) => {
                    setCountryAddress((o) => {
                      if (o !== v) {
                        setStateAddress('');
                        setCityAddress('');
                      }
                      return v;
                    });
                    setInvalid({});
                  }}
                  autocompleteApi='country'
                  autocompleteParams={{
                    limit: 300
                  }}
                />
              </div>
              <div className={'mc-grow'}>
                <AutoCompleteField
                  template={6}
                  label={LANG_CONST.address.state}
                  name={'stateAddress'}
                  value={stateAddress}
                  invalid={invalid}
                  onChange={(n, v) => {
                    setStateAddress((o) => {
                      if (o !== v) setCityAddress('');
                      return v;
                    });
                    setInvalid({});
                  }}
                  autocompleteParams={{
                    countryCode: addressData.countryCode,
                    limit: 100
                  }}
                  autocompleteApi='state'
                />
              </div>
            </div>
            <div className={'mc-flex-25 mc-flex-start'}>
              <div className={'mc-grow'}>
                <AutoCompleteField
                  template={6}
                  label={LANG_CONST.address.city}
                  value={cityAddress}
                  name={'cityAddress'}
                  onChange={(n, v) => {
                    setCityAddress(v);
                    setInvalid({});
                  }}
                  invalid={invalid}
                  allowNew={true}
                  autocompleteApi='city'
                  autocompleteParams={{
                    countryCode: addressData.countryCode,
                    stateCode: addressData.stateCode,
                    limit: 20
                  }}
                />
              </div>
              <div className={'mc-grow'}>
                <TextField
                  template={6}
                  label={LANG_CONST.address.zip}
                  name={'zipcode'}
                  value={zipcode}
                  onChange={(n, v) => {
                    setZipcode(v);
                    setInvalid({});
                  }}
                  invalid={invalid}
                />
              </div>
            </div>
            <TextField
              template={6}
              label={LANG_CONST.address.address_line}
              name={'address'}
              placeholder={''}
              value={address}
              onChange={(n, v) => {
                setAddress(v);
                setInvalid({});
              }}
              invalid={invalid}
            />
            <div className={'mc-flex-25 mc-flex-start mc-mb10'}>
              <div className={'mc-grow'}>
                <TextField
                  template={6}
                  defaultCountryName={model.state.tempCountryAddress}
                  name={'phoneNumber'}
                  type='phone'
                  value={phoneNumber}
                  onChange={(n, v) => {
                    setPhoneNumber(v);
                    setInvalid({});
                  }}
                  label={LANG_CONST.phone_number}
                  invalid={invalid}
                />
              </div>
              <div className={'mc-grow'} />
            </div>
          </div>
        </div>
        <div className='mc-modal-footer' style={{ gap: remCss(20) }}>
          <div style={{ flex: 1 }} />
          <MCButton
            label={LANG_CONST.buttons.cancel}
            light
            radius
            onClick={hide}
            disabled={loading}
          />
          <MCButton
            label={isEdit ? LANG_CONST.buttons.save : LANG_CONST.buttons.send_request}
            dark
            radius
            style={{ width: remCss(180) }}
            disabled={loading}
            loading={loading}
            onClick={save}
          />
        </div>
      </>
    </div>
  ) : (
    <InfoAlert
      screenDetail={screenDetail}
      maxWidth={remCss(650)}
      title={LANG_CONST.visit_card.cards_ordered_successfully_title}
      titleTemplate={'heading'}
      titleEx={
        <MCText
          heading
          size={20}
          weight={400}
          label={lang.t(LANG_CONST.visit_card.cards_ordered_successfully, {
            address: address + ' ' + addressData.fullAddressNameReverse + ' ' + zipcode,
            date: date && moment(date).add(5, 'day').format('DD MMMM YYYY')
          })}
        />
      }
      close={false}
      hide={hide}
      actionButton={{
        label: LANG_CONST.buttons.ok,
        action: ({ hide }) => hide()
      }}
    />
  );
};

export const VisitCard = ({ doctor, scale = 1, onClick }) => {
  const ParentComp = onClick ? View : Link;
  return (
    <ParentComp
      data-testid='visit-card'
      className={`mc-shadow mc-pointer`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'content-box',
        padding: remCss(27) * scale,
        margin: remCss(15) * scale,
        borderRadius: remCss(20) * scale,
        width: remCss(506 - 27 * 2) * scale,
        minHeight: remCss(319 - 27 * 2) * scale,
        maxHeight: remCss(319 - 27 * 2),
        backgroundColor: 'white'
      }}
      onClick={onClick}
      target={'_blank'}
      to={apiPath + `/visitCardOrder/image?id=${doctor.id}`}
    >
      <View
        style={{
          flexDirection: 'row',
          gap: remCss(14) * scale
        }}
      >
        <Avatar
          text={doctor.firstName}
          width={120 * scale}
          height={120 * scale}
          image={doctor.avatar}
        />
        <View style={{ gap: remCss(20) * scale, flex: 1 }}>
          <Logo
            titleProps={{
              style: { fontSize: remFontCss(40 * scale), marginLeft: remCss(10) * scale }
            }}
            logoProps={{
              width: 55 * scale,
              height: 55 * scale,
              fill: scssVariables[`colors-mc-primary`],
              fill2: scssVariables[`colors-mc-logo-3`]
            }}
          />
          <MCText label={userFullName(doctor, doctor.isDoctor)} size={23 * scale} weight={500} />
        </View>
      </View>
      <View style={{ flex: 1 }} />
      <View style={{ marginLeft: remCss(60) * scale }}>
        <MCText label={'lifeapp.com/' + doctor.username} size={20 * scale} weight={400} />
      </View>
      <View style={{ flex: 1 }} />
      <View style={{ gap: remCss(15) * scale, marginLeft: remCss(60) * scale }}>
        <View
          style={{ gap: remCss(15) * scale, flexDirection: 'row', marginTop: remCss(-5) * scale }}
        >
          <MCText
            numberOfLines={3}
            label={(doctor?.hospitalName || '') + ' ' + (doctor?.hospitalAddress || '')}
            size={20 * scale}
            lineHeight={20 * scale + 6 * scale}
            weight={400}
            flex
          />
          <View style={{ justifyContent: 'flex-end' }}>
            <QRCodeSVG value={'lifeapp.com/' + doctor.username} size={remCss(75 * scale)} />
          </View>
        </View>
      </View>
    </ParentComp>
  );
};

const TimelineItem = ({ children, status = 0, iconname }) => {
  const size = 50;
  return (
    <View style={{ flexDirection: 'row', zIndex: 1, gap: remCss(20) }}>
      <View style={{ backgroundColor: 'white', justifyContent: 'center', height: size + 4 }}>
        {status < 0 ? (
          <MCButton
            circle
            outline
            tiny
            style={{
              width: remCss(size),
              height: remCss(size),
              borderWidth: 2,
              boxSizing: 'border-box',
              borderColor: rgba(scssVariables['colors-mc-heading'], 0.1)
            }}
          >
            <span
              style={{
                width: remCss(size / 2 - 5),
                borderRadius: '50%',
                height: remCss(size / 2 - 5),
                borderWidth: 5,
                borderStyle: 'solid',
                borderColor: rgba(scssVariables['colors-mc-heading'], 0.2)
              }}
            />
          </MCButton>
        ) : status === 0 ? (
          <SVGIcon
            iconname={iconname}
            circle
            width={size}
            light
            outline
            padding={6}
            fill={scssVariables['colors-mc-primary']}
          />
        ) : (
          <SVGIcon
            iconname={BASE_ICONS.CHECK}
            circle
            width={size}
            dark
            padding={6}
            fill={scssVariables['colors-mc-primary']}
          />
        )}
      </View>
      <View style={{ gap: remCss(5) }}>{children}</View>
    </View>
  );
};

const OrderTimeline = ({ item, doctor, model, setItem }) => {
  let button = (
    <MCButton
      label={LANG_CONST.buttons.order_card_free}
      dark
      radius
      large
      flex
      onClick={async () => {
        popupModel.setActivePopup('order_doctor_card', {
          active: true,
          component: DoctorCardInput,
          props: {
            model,
            doctor,
            afterSave: (res) => {
              setItem(res.data);
            }
          }
        });
      }}
    />
  );

  if (!item || [CARD_STATUS.cancelled_by_doctor, CARD_STATUS.cancelled].includes(item.status)) {
    return (
      <View
        style={{
          justifyContent: 'center',
          width: '100%',
          maxWidth: 550,
          margin: 'auto',
          gap: remCss(20)
        }}
      >
        <MCText
          label={LANG_CONST.visit_card.order_your_cards}
          size={25}
          weight={500}
          style={{ textAlign: 'center' }}
        />
        <MCText label={LANG_CONST.visit_card.card_order} size={23} weight={400} />
        <View style={{ marginHorizontal: 'auto', maxWidth: 300, width: '100%' }}>{button}</View>
      </View>
    );
  }

  const editAddress = (
    <MCText
      label={LANG_CONST.buttons.edit_address}
      size={18}
      weight={500}
      heading
      className={'mc-pointer'}
      onClick={async () => {
        const res = await api('/visitCardOrder/detail', {});
        popupModel.setActivePopup('order_doctor_card', {
          active: true,
          component: DoctorCardInput,
          props: {
            item: res.data,
            model,
            isEdit: true,
            doctor,
            afterSave: (res) => {
              setItem({ ...item, ...res.data });
            }
          }
        });
      }}
    />
  );

  const timelineIndex = [
    CARD_STATUS.pending,
    CARD_STATUS.processed,
    CARD_STATUS.cancelled,
    CARD_STATUS.delivered
  ].indexOf(item.status);

  const timeline = [
    <TimelineItem key={1} status={timelineIndex} iconname={BASE_ICONS.CARD}>
      <MCText label={LANG_CONST.visit_card.order_placed} size={22} />
      <MCText label={LANG_CONST.visit_card.order_placed_des} size={20} weight={400} />

      {timelineIndex === 0 && (
        <MCText
          label={LANG_CONST.visit_card.order_placed_waiting_approval}
          size={20}
          weight={400}
          primary
        />
      )}
      {timelineIndex === 0 && editAddress}
    </TimelineItem>,
    <TimelineItem key={2} status={timelineIndex - 1} iconname={BASE_ICONS.CLIPBOARD}>
      <MCText
        label={LANG_CONST.visit_card.processing}
        size={22}
        textLight={timelineIndex - 1 < 0}
      />
      <MCText
        label={LANG_CONST.visit_card.processing_des}
        size={20}
        weight={400}
        textLight={timelineIndex - 1 < 0}
      />
    </TimelineItem>,
    <TimelineItem key={3} status={timelineIndex - 2}>
      <MCText label={LANG_CONST.visit_card.delivered} size={22} textLight={timelineIndex - 2 < 0} />
      <MCText
        label={LANG_CONST.visit_card.delivered_des}
        size={20}
        weight={400}
        textLight={timelineIndex - 2 < 0}
      />
    </TimelineItem>
  ];

  if (item.status !== CARD_STATUS.delivered) {
    button = (
      <MCButton
        label={LANG_CONST.buttons.cancel}
        onClick={() => {
          AlertManager.popup(AlertManager.TYPES.WARN, {
            close: false,
            title: LANG_CONST.notice.cancel,
            cancelButton: {
              label: LANG_CONST.buttons.no,
              action: (actions) => actions.hide()
            },
            actionButton: {
              label: LANG_CONST.buttons.yes,
              action: async (actions) => {
                actions.setLoading(true);

                const { err, data } = await api('/visitCardOrder/status', {
                  id: item.id,
                  status:
                    item.status === CARD_STATUS.pending
                      ? CARD_STATUS.cancelled
                      : CARD_STATUS.cancelled_by_doctor
                });
                if (!err) {
                  setItem({ ...item, ...data });
                }
                actions.setLoading(false);
                actions.hide();
              }
            }
          });
        }}
        quaternary
        light
        radius
        flex
        large
      />
    );
  }

  return (
    <View
      style={{
        gap: remCss(20),
        justifyContent: 'center',
        marginLeft: remCss(100),
        width: '100%',
        maxWidth: 550
      }}
    >
      <MCText label={'Order Timeline'} size={24} />
      <View style={{ position: 'relative', gap: remCss(20) }}>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 24,
            width: 0,
            height: '80%',
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: rgba(scssVariables['colors-mc-heading'], 0.2)
          }}
        />
        {timeline}
      </View>
      <View style={{ flexDirection: 'row', marginTop: remCss(20) }}>{button}</View>
    </View>
  );
};

export const DoctorCard = ({ doctor, model }) => {
  const [loading, setLoading] = React.useState(true);
  const [item, setItem] = React.useState(null);
  const visitCard = model?.state?.visitCard;
  const [resolvedDoctorId, setResolvedDoctorId] = React.useState(() =>
    visitCard && doctor?.id === visitCard?.userId ? doctor?.id : null
  );
  const hasCurrentVisitCard = visitCard && doctor?.id === visitCard?.userId;
  const hasCurrentItem = item && doctor?.id === item?.userId;
  const currentItem = hasCurrentVisitCard ? visitCard : hasCurrentItem ? item : null;
  const currentLoading = !currentItem && (resolvedDoctorId !== doctor?.id || loading);

  const refresh = useCallback(
    (data) => {
      if (data?.removed) {
        if (data.userId) setResolvedDoctorId(data.userId);
        model?.setStateSilent({ visitCard: null });
        return setItem(null);
      } else if (data) {
        if (data.userId) setResolvedDoctorId(data.userId);
        model?.setStateSilent({ visitCard: { ...(model?.state?.visitCard || {}), ...data } });
        return setItem((o) => ({ ...(o || {}), ...data }));
      }

      const currentDoctorId = doctor?.id || null;
      setLoading(true);
      const req = api('/visitCardOrder/detail', {});

      req.then((res) => {
        if (res.error === 'cancel') return;

        if (!res.error) {
          setItem(res.data);
          model?.setStateSilent({ visitCard: res.data });
        }
        setResolvedDoctorId(currentDoctorId);
        setLoading(false);
      });

      return req;
    },
    [doctor?.id]
  );

  useEffect(() => {
    events.on('ws:visit_card_order', refresh);

    return () => {
      events.cancel('ws:visit_card_order', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (visitCard && doctor?.id === visitCard?.userId) return undefined;

    let req;
    const timeoutId = window.setTimeout(() => {
      req = refresh();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      req?.cancel && req.cancel();
    };
  }, [doctor?.id, refresh, visitCard]);

  return (
    <View
      style={{
        marginLeft: remCss(-15),
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20
      }}
    >
      <VisitCard doctor={doctor} />
      {!currentLoading ? (
        <OrderTimeline item={currentItem} doctor={doctor} model={model} setItem={setItem} />
      ) : (
        <View style={{ justifyContent: 'center', margin: 'auto' }}>
          <Spinner />
        </View>
      )}
    </View>
  );
};
